import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import Header from '@/components/Header';
import {
  Lightning,
  ListChecks,
  Plus,
  Trash,
  PencilSimple,
  MagnifyingGlass,
  Star,
  FlagBanner,
  CalendarBlank,
  Note,
  X,
  FloppyDisk,
  CaretDown,
  CaretUp,
  Briefcase,
  Tag,
  Bell
} from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;

const DUE_DATE_OPTIONS = [
  { value: '', label: 'No default due date' },
  { value: 'today', label: 'Due today' },
  { value: 'tomorrow', label: 'Due tomorrow' },
  { value: 'in_3_days', label: 'Due in 3 days' },
  { value: 'in_7_days', label: 'Due in 7 days' },
  { value: 'in_14_days', label: 'Due in 2 weeks' },
  { value: 'next_monday', label: 'Due next Monday' },
  { value: 'next_friday', label: 'Due next Friday' },
  { value: 'end_of_week', label: 'Due end of week' },
];

const DUE_DATE_LABELS = Object.fromEntries(DUE_DATE_OPTIONS.filter(o => o.value).map(o => [o.value, o.label]));

// ─── Workflow Template Card ────────────────────────────
function WorkflowCard({ tmpl, expanded, onToggle, onEdit, onDelete }) {
  const checklistCount = (tmpl.checklist_items || []).length;
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden hover:border-slate-300 transition-colors" data-testid={`wf-card-${tmpl.wf_template_id}`}>
      <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={onToggle}>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${tmpl.is_system ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-navy'}`}>
          {tmpl.is_system ? <Star size={16} weight="fill" /> : <Lightning size={16} weight="fill" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{tmpl.name}</p>
          <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
            <span className="truncate">{tmpl.default_title}</span>
            {checklistCount > 0 && <span className="flex-shrink-0">{checklistCount} steps</span>}
            {tmpl.is_system && <span className="text-amber-500 font-medium flex-shrink-0">System</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {!tmpl.is_system && (
            <>
              <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="text-slate-300 hover:text-navy transition-colors p-1" data-testid={`edit-wf-${tmpl.wf_template_id}`}><PencilSimple size={14} /></button>
              <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-slate-300 hover:text-red-500 transition-colors p-1" data-testid={`delete-wf-${tmpl.wf_template_id}`}><Trash size={14} /></button>
            </>
          )}
          {expanded ? <CaretUp size={14} className="text-slate-400" /> : <CaretDown size={14} className="text-slate-400" />}
        </div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                {tmpl.default_project_client && <Detail icon={<Briefcase size={10} />} label="Project" value={tmpl.default_project_client} />}
                {tmpl.default_priority && <Detail icon={<FlagBanner size={10} />} label="Priority" value={tmpl.default_priority} />}
                {tmpl.due_date_rule && <Detail icon={<CalendarBlank size={10} />} label="Due Date" value={DUE_DATE_LABELS[tmpl.due_date_rule] || tmpl.due_date_rule} />}
                {tmpl.reminder_days_before != null && tmpl.reminder_days_before > 0 && <Detail icon={<Bell size={10} />} label="Reminder" value={`${tmpl.reminder_days_before} day${tmpl.reminder_days_before !== 1 ? 's' : ''} before`} />}
              </div>
              {tmpl.tags && tmpl.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {tmpl.tags.map((tag, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-[11px] font-medium text-navy bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-md">
                      <Tag size={10} weight="bold" />{tag}
                    </span>
                  ))}
                </div>
              )}
              {tmpl.notes && <div className="bg-slate-50 rounded-lg p-2"><p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-0.5">Notes</p><p className="text-xs text-slate-600">{tmpl.notes}</p></div>}
              {checklistCount > 0 && (
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-1">{checklistCount} steps</p>
                  <ul className="space-y-0.5">{tmpl.checklist_items.map((item, i) => <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-slate-400 flex-shrink-0" />{item}</li>)}</ul>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Detail({ icon, label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2">
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-0.5 flex items-center gap-1">{icon} {label}</p>
      <p className="text-xs text-slate-700 capitalize">{value}</p>
    </div>
  );
}

// ─── Checklist Template Card ───────────────────────────
function ChecklistCard({ tmpl, expanded, onToggle, onEdit, onDelete }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden hover:border-slate-300 transition-colors" data-testid={`cl-card-${tmpl.template_id}`}>
      <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={onToggle}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-teal-50 text-teal-600">
          <ListChecks size={16} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{tmpl.name}</p>
          <p className="text-xs text-slate-400">{tmpl.items.length} items{tmpl.description ? ` · ${tmpl.description}` : ''}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="text-slate-300 hover:text-navy transition-colors p-1" data-testid={`edit-cl-${tmpl.template_id}`}><PencilSimple size={14} /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-slate-300 hover:text-red-500 transition-colors p-1" data-testid={`delete-cl-${tmpl.template_id}`}><Trash size={14} /></button>
          {expanded ? <CaretUp size={14} className="text-slate-400" /> : <CaretDown size={14} className="text-slate-400" />}
        </div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-4 pb-4 border-t border-slate-100 pt-3">
              <ul className="space-y-1">{tmpl.items.map((item, i) => <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-teal-400 flex-shrink-0" />{item}</li>)}</ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Inline Create/Edit Forms ──────────────────────────
function WorkflowForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    default_title: initial?.default_title || '',
    default_project_client: initial?.default_project_client || '',
    default_priority: initial?.default_priority || 'medium',
    due_date_rule: initial?.due_date_rule || '',
    notes: initial?.notes || '',
    checklist_items: (initial?.checklist_items || []).join('\n'),
    tags: (initial?.tags || []).join(', '),
    reminder_days_before: initial?.reminder_days_before ?? '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.default_title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        default_title: form.default_title.trim(),
        default_project_client: form.default_project_client.trim() || undefined,
        default_priority: form.default_priority,
        due_date_rule: form.due_date_rule || undefined,
        notes: form.notes.trim() || undefined,
        checklist_items: form.checklist_items.split('\n').map(s => s.trim()).filter(Boolean),
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
        reminder_days_before: form.reminder_days_before !== '' ? parseInt(form.reminder_days_before) || undefined : undefined,
      };
      await onSave(payload);
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-blue-200 rounded-lg p-4 space-y-3" data-testid="workflow-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Playbook Name *</label>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm" data-testid="wf-form-name" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Default Task Title *</label>
          <input type="text" value={form.default_title} onChange={(e) => setForm({ ...form, default_title: e.target.value })} required className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm" data-testid="wf-form-title" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Project / Client</label>
          <input type="text" value={form.default_project_client} onChange={(e) => setForm({ ...form, default_project_client: e.target.value })} className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm" data-testid="wf-form-project" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Priority</label>
          <select value={form.default_priority} onChange={(e) => setForm({ ...form, default_priority: e.target.value })} className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm" data-testid="wf-form-priority">
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Due Date Rule</label>
          <select value={form.due_date_rule} onChange={(e) => setForm({ ...form, due_date_rule: e.target.value })} className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm" data-testid="wf-form-due">
            {DUE_DATE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Tags (comma-separated)</label>
          <input type="text" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="compliance, regulatory, recurring" className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm" data-testid="wf-form-tags" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Remind (days before due)</label>
          <input type="number" min="0" max="90" value={form.reminder_days_before} onChange={(e) => setForm({ ...form, reminder_days_before: e.target.value })} placeholder="e.g., 3" className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm" data-testid="wf-form-reminder" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Notes / Instructions</label>
        <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm" data-testid="wf-form-notes" />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Checklist Steps (one per line)</label>
        <textarea value={form.checklist_items} onChange={(e) => setForm({ ...form, checklist_items: e.target.value })} rows={3} className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm resize-none" data-testid="wf-form-checklist" />
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving || !form.name.trim() || !form.default_title.trim()} className="bg-navy text-white hover:bg-navy-hover font-medium px-4 py-2 rounded-lg text-sm disabled:opacity-50 flex items-center gap-1.5" data-testid="wf-form-save">
          <FloppyDisk size={14} weight="fill" />{saving ? 'Saving...' : initial ? 'Update' : 'Create'}
        </button>
        <button type="button" onClick={onCancel} className="text-slate-600 border border-slate-200 hover:bg-slate-50 font-medium px-4 py-2 rounded-lg text-sm">Cancel</button>
      </div>
    </form>
  );
}

function ChecklistForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    description: initial?.description || '',
    items: (initial?.items || []).join('\n'),
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const items = form.items.split('\n').map(s => s.trim()).filter(Boolean);
    if (!form.name.trim() || items.length === 0) return;
    setSaving(true);
    try {
      await onSave({ name: form.name.trim(), description: form.description.trim() || undefined, items });
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-teal-200 rounded-lg p-4 space-y-3" data-testid="checklist-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Template Name *</label>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm" data-testid="cl-form-name" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
          <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm" data-testid="cl-form-desc" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Checklist Items * (one per line)</label>
        <textarea value={form.items} onChange={(e) => setForm({ ...form, items: e.target.value })} rows={4} className="bg-white border border-slate-200 rounded-lg px-3 py-2 w-full text-sm resize-none" data-testid="cl-form-items" />
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving || !form.name.trim()} className="bg-teal-600 text-white hover:bg-teal-700 font-medium px-4 py-2 rounded-lg text-sm disabled:opacity-50 flex items-center gap-1.5" data-testid="cl-form-save">
          <FloppyDisk size={14} weight="fill" />{saving ? 'Saving...' : initial ? 'Update' : 'Create'}
        </button>
        <button type="button" onClick={onCancel} className="text-slate-600 border border-slate-200 hover:bg-slate-50 font-medium px-4 py-2 rounded-lg text-sm">Cancel</button>
      </div>
    </form>
  );
}

// ─── Main Templates Page ───────────────────────────────
function Templates({ user }) {
  const [tab, setTab] = useState('workflow');
  const [wfTemplates, setWfTemplates] = useState([]);
  const [clTemplates, setClTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [wfRes, clRes] = await Promise.all([
        axios.get(`${API}/workflow-templates`, { withCredentials: true }),
        axios.get(`${API}/checklist-templates`, { withCredentials: true }),
      ]);
      setWfTemplates(wfRes.data);
      setClTemplates(clRes.data);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetForm = () => { setShowForm(false); setEditingItem(null); };

  // ── Workflow CRUD ──
  const createWorkflow = async (payload) => {
    await axios.post(`${API}/workflow-templates`, payload, { withCredentials: true });
    resetForm();
    fetchAll();
  };
  const updateWorkflow = async (payload) => {
    await axios.put(`${API}/workflow-templates/${editingItem.wf_template_id}`, payload, { withCredentials: true });
    resetForm();
    fetchAll();
  };
  const deleteWorkflow = async (id) => {
    if (!window.confirm('Delete this workflow template?')) return;
    await axios.delete(`${API}/workflow-templates/${id}`, { withCredentials: true });
    fetchAll();
  };

  // ── Checklist CRUD ──
  const createChecklist = async (payload) => {
    await axios.post(`${API}/checklist-templates`, payload, { withCredentials: true });
    resetForm();
    fetchAll();
  };
  const updateChecklist = async (payload) => {
    await axios.put(`${API}/checklist-templates/${editingItem.template_id}`, payload, { withCredentials: true });
    resetForm();
    fetchAll();
  };
  const deleteChecklist = async (id) => {
    if (!window.confirm('Delete this checklist template?')) return;
    await axios.delete(`${API}/checklist-templates/${id}`, { withCredentials: true });
    fetchAll();
  };

  const filteredWf = search.trim()
    ? wfTemplates.filter(t => t.name.toLowerCase().includes(search.toLowerCase()) || t.default_title.toLowerCase().includes(search.toLowerCase()))
    : wfTemplates;
  const filteredCl = search.trim()
    ? clTemplates.filter(t => t.name.toLowerCase().includes(search.toLowerCase()))
    : clTemplates;

  const systemWf = filteredWf.filter(t => t.is_system);
  const userWf = filteredWf.filter(t => !t.is_system);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header user={user} />
      <main className="max-w-4xl mx-auto p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-slate-900 mb-1">Templates</h1>
            <p className="text-sm md:text-base text-slate-500">Manage your reusable workflow and checklist templates</p>
          </div>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-navy text-white hover:bg-navy-hover transition-colors shadow-sm font-medium px-4 py-2 rounded-lg flex items-center gap-2"
            data-testid="create-template-btn"
          >
            <Plus size={20} weight="bold" />
            <span className="hidden sm:inline">New Template</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 mb-6" data-testid="template-tabs">
          <button
            onClick={() => { setTab('workflow'); resetForm(); setSearch(''); }}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === 'workflow' ? 'bg-navy text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            data-testid="tab-workflow"
          >
            <Lightning size={16} weight={tab === 'workflow' ? 'fill' : 'regular'} />
            Workflow Playbooks
          </button>
          <button
            onClick={() => { setTab('checklist'); resetForm(); setSearch(''); }}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === 'checklist' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            data-testid="tab-checklist"
          >
            <ListChecks size={16} weight={tab === 'checklist' ? 'fill' : 'regular'} />
            Checklist Templates
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${tab === 'workflow' ? 'workflow' : 'checklist'} templates...`}
            className="bg-white border border-slate-200 rounded-lg pl-9 pr-4 py-2.5 w-full text-sm text-slate-900 focus:border-navy focus:ring-2 focus:ring-blue-100"
            data-testid="template-search"
          />
        </div>

        {/* Create/Edit Form */}
        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-5 overflow-hidden">
              {tab === 'workflow' ? (
                <WorkflowForm initial={editingItem} onSave={editingItem ? updateWorkflow : createWorkflow} onCancel={resetForm} />
              ) : (
                <ChecklistForm initial={editingItem} onSave={editingItem ? updateChecklist : createChecklist} onCancel={resetForm} />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Template List */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent" /></div>
        ) : tab === 'workflow' ? (
          filteredWf.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center" data-testid="no-wf-templates">
              <Lightning size={32} className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">{wfTemplates.length === 0 ? 'No workflow templates yet. Create your first one!' : 'No matching templates.'}</p>
            </div>
          ) : (
            <div className="space-y-5">
              {systemWf.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Starter Templates</p>
                  <div className="space-y-2">
                    {systemWf.map(t => <WorkflowCard key={t.wf_template_id} tmpl={t} expanded={expanded === t.wf_template_id} onToggle={() => setExpanded(expanded === t.wf_template_id ? null : t.wf_template_id)} onEdit={() => {}} onDelete={() => {}} />)}
                  </div>
                </div>
              )}
              {userWf.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Your Templates</p>
                  <div className="space-y-2">
                    {userWf.map(t => <WorkflowCard key={t.wf_template_id} tmpl={t} expanded={expanded === t.wf_template_id} onToggle={() => setExpanded(expanded === t.wf_template_id ? null : t.wf_template_id)} onEdit={() => { setEditingItem(t); setShowForm(true); }} onDelete={() => deleteWorkflow(t.wf_template_id)} />)}
                  </div>
                </div>
              )}
            </div>
          )
        ) : (
          filteredCl.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center" data-testid="no-cl-templates">
              <ListChecks size={32} className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">{clTemplates.length === 0 ? 'No checklist templates yet. Create your first one!' : 'No matching templates.'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCl.map(t => <ChecklistCard key={t.template_id} tmpl={t} expanded={expanded === t.template_id} onToggle={() => setExpanded(expanded === t.template_id ? null : t.template_id)} onEdit={() => { setEditingItem(t); setShowForm(true); }} onDelete={() => deleteChecklist(t.template_id)} />)}
            </div>
          )
        )}
      </main>
    </div>
  );
}

export default Templates;
